import { createSourcesPickerBus } from './SourcesPickerBus';
import NavigationUtils from './NavigationUtils';
import {
	clearSourcesPickerPack,
	readSourcesPickerPack,
	sessionIdFromPackStorageKey,
} from './sourcesPickerStorage';
import { persistSourceMediaPack, resolvePackSignature, setSourceMediaDragging, stampPendingAgencyMediaPack } from './agencyMediaTransfer';
import { Display } from '../enums';

/**
 * @typedef {{ source?: string|null, identifier?: string|number|null, agency_id?: string|null, signature?: string|null, blocks?: unknown[] }} SourcesPickerPack
 */

/** @type {{ sessionId: string, popupUrl: string, bus: ReturnType<typeof createSourcesPickerBus>, keepAliveAfterOffcanvasClose: boolean, unbindStorage: (() => void)|null, unbindPageHide: (() => void)|null } | null} */
let activeHost = null;

/**
 * @param {string|null|undefined} popupUrl
 * @returns {boolean}
 */
export function isSourcesArchivePicker(popupUrl) {
	if (!popupUrl) {
		return false;
	}

	try {
		const url = new URL(popupUrl, window.location.origin);
		const haystack = `${url.pathname}${url.search}${popupUrl}`.toLowerCase();
		return /agencies|collaborators|sources|recats|archive|whatsapp|importstories|milenium/i.test(haystack);
	} catch {
		return /agencies|collaborators|sources|recats|archive|whatsapp|importstories|milenium/i.test(String(popupUrl));
	}
}

/**
 * @param {SourcesPickerPack} pack
 */
export function applyIncomingSourcesPickerPack(pack) {
	if (!pack || !Array.isArray(pack.blocks) || pack.blocks.length === 0) {
		return;
	}

	const source = pack.source != null ? String(pack.source) : null;
	const identifier = pack.identifier != null && pack.identifier !== '' ? String(pack.identifier) : null;
	const normalized = {
		source,
		identifier,
		agency_id: identifier,
		signature: resolvePackSignature(pack),
		blocks: pack.blocks,
	};

	// Host tab cannot read the detached tab's sessionStorage — persist locally for drop fallbacks.
	stampPendingAgencyMediaPack(normalized);
	persistSourceMediaPack(normalized);
	setSourceMediaDragging(true);
}

/**
 * @param {{ visible: boolean, sessionId?: string|null, popupUrl?: string|null }} options
 */
function setDetachButtonState({ visible, sessionId = null, popupUrl = null }) {
	const $offcanvas = $('.offcanvas-right');
	const $btn = $offcanvas.find('[data-sources-picker-detach]');

	if (!$btn.length) {
		return;
	}

	$btn.toggleClass('d-none', !visible);

	if (visible && sessionId) {
		$offcanvas.attr('data-picker-session', sessionId);
		$btn.attr('data-picker-session', sessionId);
		if (popupUrl) {
			$offcanvas.attr('data-picker-popup-url', popupUrl);
			$btn.attr('data-picker-popup-url', popupUrl);
		}
		return;
	}

	$offcanvas.removeAttr('data-picker-session');
	$offcanvas.removeAttr('data-picker-popup-url');
	$btn.removeAttr('data-picker-session');
	$btn.removeAttr('data-picker-popup-url');
}

/**
 * @param {string} url
 * @returns {string}
 */
function pathnameOf(url) {
	try {
		return new URL(url, window.location.origin).pathname.replace(/\/$/, '');
	} catch {
		return String(url);
	}
}

/**
 * @param {string} currentUrl
 * @returns {{url: string, icon?: string, label: string, alert?: boolean}|null}
 */
function matchSourcesPickerLink(currentUrl) {
	const links = Array.isArray(window.naxosSourcesPickerLinks) ? window.naxosSourcesPickerLinks : [];
	const currentPath = pathnameOf(currentUrl);

	return (
		links.find((link) => pathnameOf(link.url) === currentPath) ||
		links.find((link) => currentPath.includes(pathnameOf(link.url))) ||
		null
	);
}

/**
 * @param {string} label
 */
function setSourcesPickerTitleLabel(label) {
	const $offcanvas = $('.offcanvas-right');
	$offcanvas.find('[data-sources-picker-current-label]').text(label);
	$offcanvas.find('.offcanvas-title-static').text(label);
}

/**
 * Navigate the embedded sources iframe to another source list.
 *
 * @param {string} targetUrl
 */
function navigateSourcesPickerIframe(targetUrl) {
	const finalUrl = NavigationUtils.appendPopupQueryParams(targetUrl, {
		offcanvas: true,
		display: Display.CARD_ESSENTIAL,
	});

	const iframe = document.querySelector('#offcanvas-content iframe.offcanvas-iframe, #offcanvas-content iframe');
	if (!iframe) {
		NavigationUtils.openUrlInOffcanvas(finalUrl);
	} else {
		const spinner = document.getElementById('offcanvas-loading-spinner');
		if (spinner) {
			spinner.style.display = 'flex';
		}
		iframe.style.display = 'none';
		iframe.addEventListener(
			'load',
			() => {
				if (spinner) {
					spinner.style.display = 'none';
				}
				iframe.style.display = 'block';
			},
			{ once: true },
		);
		iframe.src = finalUrl;
	}

	if (activeHost) {
		activeHost.popupUrl = finalUrl;
		setDetachButtonState({
			visible: true,
			sessionId: activeHost.sessionId,
			popupUrl: finalUrl,
		});
	}

	const matched = matchSourcesPickerLink(finalUrl);
	if (matched?.label) {
		setSourcesPickerTitleLabel(matched.label);
	}

	enableSourcesPickerSwitcher(finalUrl);
}

/**
 * Show the offcanvas title as a source switcher dropdown (attached mode only).
 *
 * @param {string} currentUrl
 */
export function enableSourcesPickerSwitcher(currentUrl) {
	const $offcanvas = $('.offcanvas-right');
	const $switcher = $offcanvas.find('[data-sources-picker-switcher]');
	const $menu = $offcanvas.find('[data-sources-picker-menu]');
	const $static = $offcanvas.find('.offcanvas-title-static');
	const links = Array.isArray(window.naxosSourcesPickerLinks) ? window.naxosSourcesPickerLinks : [];

	if (!$switcher.length || links.length === 0) {
		return;
	}

	const matched = matchSourcesPickerLink(currentUrl);
	setSourcesPickerTitleLabel(matched?.label || $static.text() || 'Fonti');

	$menu.empty();
	for (const link of links) {
		const isActive = matched && pathnameOf(matched.url) === pathnameOf(link.url);
		const $item = $('<button type="button" class="dropdown-item d-flex align-items-center gap-2"></button>');
		if (link.icon) {
			$item.append($('<i></i>').addClass(link.icon));
		}
		$item.append($('<span></span>').text(link.label));
		if (link.alert) {
			$item.append($('<i class="fa-solid fa-circle-exclamation text-danger ml-auto"></i>'));
		}
		if (isActive) {
			$item.addClass('active');
		}
		$item.on('click', (event) => {
			event.preventDefault();
			navigateSourcesPickerIframe(link.url);
		});
		$menu.append($item);
	}

	$static.addClass('d-none');
	$switcher.removeClass('d-none');
}

/**
 * Restore the plain offcanvas title (non-sources / closed picker).
 */
export function disableSourcesPickerSwitcher() {
	const $offcanvas = $('.offcanvas-right');
	$offcanvas.find('[data-sources-picker-switcher]').addClass('d-none');
	$offcanvas.find('[data-sources-picker-menu]').empty();
	$offcanvas.find('.offcanvas-title-static').removeClass('d-none');
}

/**
 * @param {{ sessionId: string, popupUrl: string }} options
 */
export function startSourcesPickerHost({ sessionId, popupUrl }) {
	stopSourcesPickerHost({ notifyDetached: true });

	const bus = createSourcesPickerBus({ sessionId, role: 'host' });

	bus.onMessage((message) => {
		if (message.kind === 'DRAG_PACK' && message.payload) {
			applyIncomingSourcesPickerPack(/** @type {SourcesPickerPack} */ (message.payload));
		}

		if (message.kind === 'DRAG_END') {
			window.setTimeout(() => setSourceMediaDragging(false), 2000);
		}

		if (message.kind === 'DETACHED_CLOSED') {
			stopSourcesPickerHost({ notifyDetached: false });
		}
	});

	const onStorage = (event) => {
		const storageSessionId = sessionIdFromPackStorageKey(event.key);
		if (!storageSessionId || storageSessionId !== sessionId) {
			return;
		}

		const pack = readSourcesPickerPack(sessionId);
		if (pack) {
			applyIncomingSourcesPickerPack(pack);
		}
	};

	window.addEventListener('storage', onStorage);

	const onPageHide = () => {
		stopSourcesPickerHost({ notifyDetached: true });
	};

	window.addEventListener('pagehide', onPageHide);

	bus.start();

	activeHost = {
		sessionId,
		popupUrl,
		bus,
		keepAliveAfterOffcanvasClose: false,
		unbindStorage: () => window.removeEventListener('storage', onStorage),
		unbindPageHide: () => window.removeEventListener('pagehide', onPageHide),
	};

	setDetachButtonState({ visible: true, sessionId, popupUrl });
	enableSourcesPickerSwitcher(popupUrl);
}

/**
 * @param {{ notifyDetached?: boolean }} [options]
 */
export function stopSourcesPickerHost({ notifyDetached = false } = {}) {
	if (!activeHost) {
		setDetachButtonState({ visible: false });
		disableSourcesPickerSwitcher();
		return;
	}

	const { sessionId, bus, unbindStorage, unbindPageHide } = activeHost;

	if (notifyDetached) {
		try {
			bus.send('HOST_CLOSED');
		} catch {
			// Ignore.
		}
	}

	clearSourcesPickerPack(sessionId);
	unbindStorage?.();
	unbindPageHide?.();
	bus.destroy();
	activeHost = null;
	setDetachButtonState({ visible: false });
	disableSourcesPickerSwitcher();
}

/**
 * Keep the host bus alive after the offcanvas is closed by a successful detach.
 */
export function markSourcesPickerDetached() {
	if (!activeHost) {
		return;
	}

	activeHost.keepAliveAfterOffcanvasClose = true;
	setDetachButtonState({ visible: false });
	disableSourcesPickerSwitcher();
}

/**
 * Called when the offcanvas closes. Stops the host unless detach kept it alive.
 */
export function onSourcesPickerOffcanvasClosed() {
	if (!activeHost) {
		return;
	}

	if (activeHost.keepAliveAfterOffcanvasClose) {
		return;
	}

	stopSourcesPickerHost({ notifyDetached: true });
}

/**
 * @returns {boolean}
 */
export function detachSourcesPicker() {
	if (!activeHost) {
		return false;
	}

	const { popupUrl, sessionId } = activeHost;
	const url = NavigationUtils.buildDetachedSourcesPickerUrl(popupUrl, sessionId);
	const tab = window.open(url, '_blank');

	if (!tab) {
		return false;
	}

	markSourcesPickerDetached();
	document.dispatchEvent(new CustomEvent('closeOffcanvas'));
	return true;
}

/**
 * @returns {string|null}
 */
export function getActiveSourcesPickerSessionId() {
	return activeHost?.sessionId ?? null;
}

/**
 * Bind the detach button once (delegated).
 */
export function bindSourcesPickerDetachButton() {
	if (typeof document === 'undefined') {
		return;
	}

	if (window.__naxosSourcesPickerDetachBound) {
		return;
	}

	window.__naxosSourcesPickerDetachBound = true;

	document.addEventListener('click', (event) => {
		const target = event.target;
		if (!(target instanceof Element)) {
			return;
		}

		const button = target.closest('[data-sources-picker-detach]');
		if (!button || button.classList.contains('d-none')) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();

		const opened = detachSourcesPicker();
		if (!opened) {
			console.warn('Sources picker detach blocked (popup blocker?)');
		}
	});
}
